import { gql } from '@apollo/client';
import type { TypedDocumentNode } from '@apollo/client';
import type { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes.ts';
import { Button } from '@sorare/core/src/atoms/buttons/Button/index.tsx';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase/index.tsx';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons/index.tsx';
import { iconChevronRight } from '@sorare/core/src/atoms/icons/system/iconChevronRight.ts';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { BrandXS, LabelL } from '@sorare/core/src/atoms/typography/index.tsx';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge/index.tsx';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig/index.tsx';
import { Avatar } from '@sorare/core/src/components/user/Avatar/index.tsx';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft/index.tsx';
import { range } from '@sorare/core/src/lib/arrays.ts';

import { TaskClaimedDialogs } from '#src/components/missions/TaskClaimedDialogs/index.tsx';
import { useTaskClaimedDialogs } from '#src/components/missions/TaskClaimedDialogs/useTaskClaimedDialogs.tsx';

import { VerticalDivider } from '@sorare/core/src/atoms/ui/Divider/index.tsx';
import { LightedBezel } from '@sorare/core/src/atoms/ui/bezels/LightedBezel/index.tsx';
import type { ReferralMission_inviteTaskAsReferrer } from './__generated__/index.graphql.ts';

const MissionCard = styled(SBHorizontal).attrs({ gap: 2, as: ButtonBase })`
  width: 100%;
  padding: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--unit);
  background: var(--c-nd-50);
`;
const ProgressSegment = styled.div`
  flex: 1;
  height: 6px;
  border-radius: 2px;
  background: var(--segment-color);
`;
const CompletedCardButton = styled(ButtonBase)`
  width: 100%;
`;

const Lvl = styled(BrandXS)`
  white-space: nowrap;
`;
const Nickname = styled(LabelL)`
  word-break: break-word;
`;

type Props = {
  task: ReferralMission_inviteTaskAsReferrer;
  onClick: () => void;
};

export const ReferralMission = ({ task, onClick }: Props) => {
  const {
    onClaim,
    pickerDialogTask,
    setPickerDialogTask,
    revealPackCardsDialogReward,
    setRevealPackCardsDialogReward,
    rewardDialogTask,
    setRewardDialogTask,
  } = useTaskClaimedDialogs();
  const { progress, target, endDate, referral, steps } = task;
  const { referee } = referral;
  const completedStep = task.steps?.find(
    step => step.aasmState === ManagerProgressionTaskState.COMPLETED
  );

  const currentRewardConfig = steps?.at(progress)?.rewardConfigs?.[0];
  const currentRewardConfigUI = currentRewardConfig ? (
    <AnyRewardConfig
      anyRewardConfig={currentRewardConfig}
      variant={Variant.CHIP}
      textVariantSize="lg"
    />
  ) : null;
  if (completedStep) {
    return (
      <>
        <CompletedCardButton onClick={onClick}>
          <NeonEdge
            color="var(--c-set-surface-primary)"
            spread="60%"
            showHalo="always"
            side="top"
          >
            <LightedBezel
              color="var(--c-set-surface-primary)"
              borderColor="var(--c-set-surface-primary)"
              bezelSize={12}
              radius={8}
              background="conic-gradient(from 90deg at 3px 3px, var(--c-nd-100) 25%, rgba(var(--c-rgb-black), 0.2) 0) 0 0/5px 5px"
            >
              <Horizontal
                gap={2}
                style={{ padding: 'var(--double-unit)', alignItems: 'center' }}
              >
                {currentRewardConfigUI}
                <Vertical gap={1} style={{ flex: 1 }}>
                  <Horizontal gap={1}>
                    <Avatar user={referee} size={4.5} rounded />
                    <Vertical gap={0.5} className="text-left">
                      <Nickname bold>
                        <FormattedMessage
                          defaultMessage="{nickname} completed"
                          values={{ nickname: referee.nickname }}
                        />
                      </Nickname>
                      <BrandXS color="var(--c-nd-500)">
                        <FormattedMessage
                          defaultMessage="Lvl {level}"
                          values={{ level: progress }}
                        />
                      </BrandXS>
                    </Vertical>
                  </Horizontal>
                  <Button
                    size="small"
                    color="primary"
                    onClick={async () => {
                      await onClaim(completedStep);
                    }}
                  >
                    <FormattedMessage defaultMessage="Claim" />
                  </Button>
                </Vertical>
              </Horizontal>
            </LightedBezel>
          </NeonEdge>
        </CompletedCardButton>
        <TaskClaimedDialogs
          pickerDialogTask={pickerDialogTask}
          onPickerDialogClose={() => {
            setPickerDialogTask(null);
          }}
          revealPackCardsDialogReward={revealPackCardsDialogReward}
          onRevealPackCardsDialogClose={() => {
            setRevealPackCardsDialogReward(null);
          }}
          rewardDialogTask={rewardDialogTask}
          onRewardDialogClose={() => {
            setRewardDialogTask(null);
          }}
        />
      </>
    );
  }

  return (
    <MissionCard onClick={onClick}>
      <Vertical gap={1.5} style={{ flex: 1 }}>
        {!!endDate && (
          <BrandXS color="var(--c-nd-500)">
            <TimeLeft time={endDate} />{' '}
            <FormattedMessage defaultMessage="left" />
          </BrandXS>
        )}
        <Horizontal gap={1}>
          <Avatar user={referee} size={4.5} rounded />
          <Vertical gap={0.5}>
            <Nickname bold className="text-left">
              {referee.nickname}
            </Nickname>
          </Vertical>
        </Horizontal>
        <Horizontal gap={2} style={{ alignItems: 'center' }}>
          <Horizontal gap={1} style={{ flex: 1 }}>
            <Horizontal gap={0.5} style={{ flex: 1 }}>
              {range(target).map(i => (
                <ProgressSegment
                  key={i}
                  style={
                    {
                      '--segment-color':
                        i < progress
                          ? 'var(--c-set-surface-primary-text)'
                          : 'var(--c-nd-50)',
                    } as CSSProperties
                  }
                />
              ))}
            </Horizontal>
            <Lvl color="var(--c-nd-600)">
              <FormattedMessage
                defaultMessage="<white>lvl {current}</white>/{total}"
                values={{
                  current: progress,
                  total: target,
                  white: chunks => (
                    <BrandXS as="span" color="var(--c-white)">
                      {chunks}
                    </BrandXS>
                  ),
                }}
              />
            </Lvl>
          </Horizontal>
          <VerticalDivider height={32} />
          {currentRewardConfigUI}
        </Horizontal>
      </Vertical>
      <FontAwesomeIcon
        icon={iconChevronRight}
        color="var(--c-nd-600)"
        size="xs"
      />
    </MissionCard>
  );
};

ReferralMission.fragments = {
  inviteTaskAsReferrer: gql`
    fragment ReferralMission_inviteTaskAsReferrer on InviteTaskAsReferrer {
      id
      aasmState
      progress
      target
      endDate
      steps {
        id
        aasmState
        rewardConfigs {
          id
          ...AnyRewardConfig_anyRewardConfigInterface
        }
        ...TaskClaimedDialogs_taskInterface
      }
      referral {
        id
        referee {
          slug
          nickname
          ...Avatar_user
        }
      }
    }
    ${TaskClaimedDialogs.fragments.taskInterface}
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
    ${Avatar.fragments.user}
  ` as TypedDocumentNode<ReferralMission_inviteTaskAsReferrer>,
};
